import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { storableError } from '../../util/errors';
import { denormalisedResponseEntities } from '../../util/data';
import { createSlug } from '../../util/urlHelpers';
import { STOCK_MULTIPLE_ITEMS } from '../../util/types';
import { getProductVariantFields } from '../../util/productVariants';
import { getCombinationStock, getVariantStockSummary } from '../../util/variantStock';
import {
  getStatesNeedingProviderAttention,
  getSupportedProcessesInfo,
  isPurchaseProcessAlias,
} from '../../transactions/transaction';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { fetchStripeAccount } from '../../ducks/stripeConnectAccount.duck';

// How far back "recent sales" looks.
export const RECENT_SALES_DAYS = 30;

// The Marketplace API returns at most 100 transactions per page. Rather than paging
// through a vendor's whole history to add up one number, the dashboard reads the most
// recent page and says so when there turns out to be more (see `recentSalesArePartial`
// below). At ethy's launch volumes one page covers everybody; when it stops doing so,
// the page tells the truth instead of quietly under-reporting.
const RECENT_SALES_PAGE_SIZE = 100;
const ATTENTION_PAGE_SIZE = 20;
const LISTINGS_PAGE_SIZE = 100;

// Below this, a vendor still has time to restock before the listing goes quiet.
export const LOW_STOCK_THRESHOLD = 3;

const entityRefs = entities => entities.map(entity => ({ id: entity.id, type: entity.type }));

const daysAgo = days => new Date(Date.now() - days * 24 * 60 * 60 * 1000);

/**
 * Boil an own listing down to the stock facts worth surfacing on the dashboard.
 *
 * Returns null for listings with nothing to say, so the caller can filter.
 */
const getStockAttention = (listing, config) => {
  const { title = '', publicData, metadata } = listing.attributes || {};
  const { listingType, transactionProcessAlias } = publicData || {};
  const listingTypeConfig = config.listing.listingTypes.find(
    conf => conf.listingType === listingType
  );

  const usesStock =
    isPurchaseProcessAlias(transactionProcessAlias) &&
    listingTypeConfig?.stockType === STOCK_MULTIPLE_ITEMS;
  if (!usesStock) {
    return null;
  }

  const currentStock = listing.currentStock?.attributes?.quantity;
  const variantFields = getProductVariantFields({
    publicData,
    metadata,
    listingFieldConfigs: config.listing.listingFields,
    categoryConfiguration: config.categoryConfiguration,
  });
  const { stockMap, combinations, isTracked, isOutOfSyncWithListingStock } = getVariantStockSummary(
    { variantFields, publicData, listingStock: currentStock }
  );
  const soldOutCount = isTracked
    ? combinations.filter(combination => getCombinationStock(stockMap, combination.key) === 0)
        .length
    : 0;

  const isOutOfStock = currentStock === 0;
  const isLowStock = currentStock > 0 && currentStock <= LOW_STOCK_THRESHOLD;

  if (!isOutOfStock && !isLowStock && soldOutCount === 0 && !isOutOfSyncWithListingStock) {
    return null;
  }

  return {
    id: listing.id.uuid,
    title,
    slug: createSlug(title),
    currentStock,
    isOutOfStock,
    isLowStock,
    soldOutCount,
    totalCombinations: combinations.length,
    isOutOfSync: isOutOfSyncWithListingStock,
  };
};

// ================ Slice ================ //

const initialState = {
  fetchInProgress: false,
  fetchError: null,
  // Sales the vendor has to do something about, most urgent first.
  attentionRefs: [],
  attentionTotal: 0,
  // The most recent sales, used for the "last 30 days" figures.
  recentSaleRefs: [],
  recentSalesArePartial: false,
  // Listings with a stock problem worth mentioning.
  stockAttention: [],
};

const sellerDashboardPageSlice = createSlice({
  name: 'SellerDashboardPage',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(loadDataThunk.pending, state => {
        state.fetchInProgress = true;
        state.fetchError = null;
      })
      .addCase(loadDataThunk.fulfilled, (state, action) => {
        const {
          attentionRefs,
          attentionTotal,
          recentSaleRefs,
          recentSalesArePartial,
          stockAttention,
        } = action.payload;
        state.fetchInProgress = false;
        state.attentionRefs = attentionRefs;
        state.attentionTotal = attentionTotal;
        state.recentSaleRefs = recentSaleRefs;
        state.recentSalesArePartial = recentSalesArePartial;
        state.stockAttention = stockAttention;
      })
      .addCase(loadDataThunk.rejected, (state, action) => {
        console.error(action.payload || action.error);
        state.fetchInProgress = false;
        state.fetchError = action.payload;
      });
  },
});

export default sellerDashboardPageSlice.reducer;

// ================ Load data ================ //

const TRANSACTION_INCLUDES = ['listing', 'listing.images', 'customer', 'customer.profileImage'];
const TRANSACTION_FIELDS = [
  'processName',
  'lastTransition',
  'lastTransitionedAt',
  'payinTotal',
  'payoutTotal',
  'lineItems',
];

const loadDataPayloadCreator = (params, { dispatch, rejectWithValue, extra: sdk }) => {
  const { config } = params || {};
  const processNames = getSupportedProcessesInfo().map(p => p.name);
  const statesNeedingAttention = getStatesNeedingProviderAttention() || [];

  // Whether this vendor can be paid at all - drives the banner at the top of the page.
  // Only worth a second request for people who started Connect onboarding.
  const payoutDetails = dispatch(fetchCurrentUser()).then(currentUser =>
    currentUser?.stripeAccount?.id ? dispatch(fetchStripeAccount()).catch(() => null) : null
  );

  const attentionSales = sdk.transactions.query({
    only: 'sale',
    states: statesNeedingAttention.map(state => `state/${state}`).join(','),
    include: TRANSACTION_INCLUDES,
    'fields.transaction': TRANSACTION_FIELDS,
    'fields.listing': ['title', 'publicData.listingType'],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'deleted', 'banned'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
    'limit.images': 1,
    perPage: ATTENTION_PAGE_SIZE,
  });

  const recentSales = sdk.transactions.query({
    only: 'sale',
    processNames,
    include: TRANSACTION_INCLUDES,
    'fields.transaction': TRANSACTION_FIELDS,
    'fields.listing': ['title'],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'deleted', 'banned'],
    'limit.images': 1,
    perPage: RECENT_SALES_PAGE_SIZE,
  });

  const ownListings = sdk.ownListings.query({
    include: ['currentStock'],
    perPage: LISTINGS_PAGE_SIZE,
  });

  return Promise.all([payoutDetails, attentionSales, recentSales, ownListings])
    .then(([_payout, attentionResponse, recentResponse, listingsResponse]) => {
      dispatch(addMarketplaceEntities(attentionResponse));
      dispatch(addMarketplaceEntities(recentResponse));

      const recentTransactions = recentResponse.data.data;
      const recentTotal = recentResponse.data.meta?.totalItems ?? recentTransactions.length;
      // The figures below only cover what was fetched. They understate reality only
      // when there are older sales we didn't see *and* everything we did see falls
      // inside the window - otherwise the cut-off happened before the window did.
      const cutoff = daysAgo(RECENT_SALES_DAYS);
      const oldestFetched = recentTransactions[recentTransactions.length - 1];
      const recentSalesArePartial =
        recentTotal > recentTransactions.length &&
        !!oldestFetched &&
        new Date(oldestFetched.attributes.createdAt) >= cutoff;

      const stockAttention = denormalisedResponseEntities(listingsResponse)
        .map(listing => getStockAttention(listing, config))
        .filter(Boolean);

      return {
        attentionRefs: entityRefs(attentionResponse.data.data),
        attentionTotal: attentionResponse.data.meta?.totalItems ?? 0,
        recentSaleRefs: entityRefs(recentTransactions),
        recentSalesArePartial,
        stockAttention,
      };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const loadDataThunk = createAsyncThunk(
  'SellerDashboardPage/loadData',
  loadDataPayloadCreator
);

export const loadData = (params, search, config) => dispatch => dispatch(loadDataThunk({ config }));
